// Life milestones and seasonal hazards, derived from dates only.

import { addDays, addMonths, daysBetween, parseISODate, today } from './time.ts';
import type { ISODate, Millis } from './types.ts';

export interface Milestone {
  id: string;
  date: ISODate;
  title: string;
  detail: string;
  icon: 'cake' | 'home' | 'syringe' | 'paw' | 'bowl' | 'tooth' | 'brain' | 'star' | 'sparkles';
}

export function milestones(dob: ISODate, homeDate?: ISODate): Milestone[] {
  const w = (n: number) => addDays(dob, n * 7);
  const list: Milestone[] = [
    { id: 'born', date: dob, title: 'Born', detail: 'Hello world, Arlo.', icon: 'star' },
    {
      id: 'social-peak',
      date: w(3),
      title: 'Socialisation window opens',
      detail: 'From about 3 weeks puppies start learning what is “normal” and safe.',
      icon: 'sparkles',
    },
  ];
  if (homeDate)
    list.push({ id: 'home', date: homeDate, title: 'Comes home', detail: 'Day one of the rest of his life with Matt and Nick.', icon: 'home' });
  list.push(
    {
      id: 'meals-3',
      date: w(12),
      title: 'Drop to 3 meals a day',
      detail: 'Same daily amount, split into three.',
      icon: 'bowl',
    },
    {
      id: 'social-close',
      date: w(14),
      title: 'Socialisation window starts to close',
      detail: 'The sensitive period tapers off around 12–16 weeks. Keep going after — but these weeks count double.',
      icon: 'brain',
    },
    {
      id: 'teething',
      date: w(14),
      title: 'Teething begins',
      detail: 'Adult teeth push through between roughly 3½ and 6–7 months. Frozen chews and lots of appropriate things to gnaw.',
      icon: 'tooth',
    },
    {
      id: 'meals-2',
      date: addMonths(dob, 6),
      title: 'Drop to 2 meals a day',
      detail: 'Around 6 months most puppies move to two meals.',
      icon: 'bowl',
    },
    {
      id: 'adolescence',
      date: addMonths(dob, 6),
      title: 'Adolescence begins',
      detail: 'Roughly 6–18 months. Expect a recall wobble and some boundary-testing. It passes!',
      icon: 'brain',
    },
    {
      id: 'adult-food',
      date: addMonths(dob, 12),
      title: 'Switch to adult food',
      detail: 'Medium breeds like cockers usually move to adult food at about 12 months — check the food manufacturer’s guidance.',
      icon: 'bowl',
    },
    { id: 'birthday-1', date: addMonths(dob, 12), title: 'First birthday', detail: 'Cake (dog-safe).', icon: 'cake' },
  );
  if (homeDate)
    list.push({ id: 'gotcha-1', date: addMonths(homeDate, 12), title: 'One year home', detail: 'Happy gotcha day!', icon: 'home' });
  return list.sort((a, b) => a.date.localeCompare(b.date));
}

export interface SeasonalAlert {
  id: string;
  title: string;
  detail: string;
  level: 'info' | 'warn';
}

/** Seasonal UK hazards relevant at this time of year. */
export function seasonalAlerts(now: Millis = Date.now()): SeasonalAlert[] {
  const d = parseISODate(today(now));
  const year = d.getFullYear();
  const inRange = (from: [number, number], to: [number, number]) => {
    const t = today(now);
    const f = `${year}-${String(from[0]).padStart(2, '0')}-${String(from[1]).padStart(2, '0')}`;
    const e = `${year}-${String(to[0]).padStart(2, '0')}-${String(to[1]).padStart(2, '0')}`;
    return f <= e ? t >= f && t <= e : t >= f || t <= e;
  };
  const alerts: SeasonalAlert[] = [];

  if (inRange([10, 1], [11, 12]) || inRange([12, 20], [1, 2])) {
    const bonfire = `${d.getMonth() === 0 ? year - 1 : year}-11-05`;
    const days = daysBetween(today(now), bonfire);
    alerts.push({
      id: 'fireworks',
      title: days > 0 && days < 40 ? `Fireworks season — Bonfire Night in ${days} days` : 'Fireworks season',
      detail:
        'Play firework sound recordings very quietly during meals and play, building up slowly. On the night: walk before dusk, close curtains, TV on, a cosy den, and stay calm and matter-of-fact.',
      level: 'warn',
    });
  }
  if (inRange([8, 15], [11, 30]))
    alerts.push({
      id: 'sci',
      title: 'Autumn woodland walks',
      detail:
        'Conkers and acorns are toxic, and “seasonal canine illness” (sudden vomiting/diarrhoea after woodland walks) peaks Aug–Nov. Tiny harvest mites (orange specks between toes) are common.',
      level: 'info',
    });
  if (inRange([12, 1], [12, 31]))
    alerts.push({
      id: 'christmas',
      title: 'Christmas hazards',
      detail:
        'Chocolate, mince pies, Christmas pudding & fruit cake (raisins!), xylitol sweets, cooked bones, onion stuffing, tinsel and baubles. Keep visitors’ bags out of reach.',
      level: 'warn',
    });
  if (inRange([11, 15], [2, 28]))
    alerts.push({
      id: 'winter',
      title: 'Winter',
      detail:
        'Antifreeze is sweet and deadly — clean up spills. Rinse road grit/salt off paws. Dry those cocker ears after wet walks. Reflective collar or light for dark evenings.',
      level: 'info',
    });
  if (inRange([3, 15], [4, 30]))
    alerts.push({
      id: 'easter',
      title: 'Spring & Easter',
      detail: 'Easter eggs and hot cross buns (raisins) are toxic. Adders wake up in spring — keep to paths on heathland.',
      level: 'warn',
    });
  if (inRange([3, 1], [10, 31]))
    alerts.push({
      id: 'ticks',
      title: 'Tick season',
      detail: 'Check his ears, armpits, between toes and belly after countryside walks. Use a tick twister, never pull or burn.',
      level: 'info',
    });
  if (inRange([5, 15], [9, 15]))
    alerts.push({
      id: 'grass-seeds',
      title: 'Grass seed season',
      detail:
        'Spaniels are grass-seed magnets. Check ears, between toes, eyes and armpits after walks. Sudden head shaking, paw licking or sneezing = vet.',
      level: 'warn',
    });
  if (inRange([6, 1], [8, 31]))
    alerts.push({
      id: 'heat',
      title: 'Hot weather',
      detail:
        'Walk early and late. If pavement is too hot for the back of your hand, it’s too hot for paws. Never leave him in a car. Beware blue-green algae in still water.',
      level: 'warn',
    });
  return alerts;
}
